import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { ChevronDown, Loader2 } from 'lucide-react';
import {
  channelLabels, channelColors, aiSourceLabels, getKanalDetail,
  type ChannelStat, type AiSourceStat,
} from '@/lib/analytics-api';

interface Props {
  channels: ChannelStat[];
  aiSources?: AiSourceStat[];
  /** Zeitraum und Projekt der Seite — das Aufklappen muss denselben Ausschnitt zeigen. */
  from?: string;
  to?: string;
  projectId?: string | null;
}

/**
 * Bei welchen Kanälen die Quelle die Antwort ist — und bei welchen die
 * Einstiegsseite. „Direkt" hat per Definition keine Quelle, und bei organisch
 * schickt Google die Suchanfrage seit 2011 nicht mehr mit; dort wäre eine
 * Quellenliste aus „google.com" eine Zeile ohne Erkenntnis.
 */
const SEITEN_ZUERST = new Set(['direct', 'organic']);

const HINWEIS: Record<string, string> = {
  organic: 'Welchen Suchbegriff jemand eingetippt hat, gibt Google nicht heraus — nur die Search Console zeigt das, gesammelt und einen Tag später.',
  direct: 'Kein Verweis in der Adresse: direkt eingetippt, aus einem Lesezeichen, aus einer App oder einer Mail.',
  paid: 'Nach Kampagne. Das Keyword steht am einzelnen Besuch in der Live-Ansicht, sobald das Konto es mitschickt.',
};

function KanalDetail({ channel, from, to, projectId }: { channel: string; from?: string; to?: string; projectId?: string | null }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['kanal-detail', channel, from, to, projectId],
    queryFn: () => getKanalDetail(channel, { from, to, projectId }),
  });

  if (isLoading) {
    return <div className="px-3 py-3 text-xs text-gray-500"><Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" /> wird geladen …</div>;
  }
  if (error) return <div className="px-3 py-3 text-xs text-red-700">{(error as Error).message}</div>;
  if (!data) return null;

  const seitenZuerst = SEITEN_ZUERST.has(channel);
  // Sortiert wird nach der Zahl, die DANEBEN steht. Die Datenbank liefert nach
  // Aufrufen sortiert; angezeigt werden Besucher — ntp.msn.com mit 16
  // Besuchern stand dadurch unter blog.holzzauber.eu mit 13, und die Liste sah
  // aus, als wäre sie gar nicht sortiert.
  const nachBesuchern = <T extends { besucher: number }>(xs: T[]) => [...xs].sort((a, b) => b.besucher - a.besucher);
  const listen = seitenZuerst
    ? [
        { titel: 'Einstiegsseiten', zeilen: nachBesuchern(data.seiten.map((s) => ({ name: s.path, besucher: s.besucher }))) },
        { titel: 'Quellen', zeilen: nachBesuchern(data.quellen.map((q) => ({ name: q.quelle, besucher: q.besucher }))) },
      ]
    : [
        { titel: channel === 'paid' ? 'Kampagnen' : 'Quellen', zeilen: nachBesuchern(data.quellen.map((q) => ({ name: q.quelle, besucher: q.besucher }))) },
        { titel: 'Einstiegsseiten', zeilen: nachBesuchern(data.seiten.map((s) => ({ name: s.path, besucher: s.besucher }))) },
      ];
  const max = Math.max(1, ...listen[0].zeilen.map((z) => z.besucher));

  return (
    <div className="bg-gray-50 px-3 py-3">
      {HINWEIS[channel] && <p className="mb-2.5 text-[11px] leading-snug text-gray-500">{HINWEIS[channel]}</p>}
      {listen.map((liste, i) => (
        liste.zeilen.length === 0 ? null : (
          <div key={liste.titel} className={i > 0 ? 'mt-3 border-t border-gray-200 pt-3' : ''}>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{liste.titel}</div>
            <div className="space-y-1">
              {liste.zeilen.slice(0, i === 0 ? 12 : 5).map((z) => (
                <div key={z.name} className="flex items-center gap-2">
                  <span className="w-52 shrink-0 truncate text-xs text-gray-700" title={z.name}>{z.name}</span>
                  <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200">
                    <span
                      className="block h-full rounded-full"
                      style={{ width: `${Math.max(3, (z.besucher / max) * 100)}%`, backgroundColor: channelColors[channel as keyof typeof channelColors] || '#6b7280' }}
                    />
                  </span>
                  <span className="w-10 shrink-0 text-right text-xs tabular-nums text-gray-500">{z.besucher}</span>
                </div>
              ))}
            </div>
          </div>
        )
      ))}
      {listen.every((l) => l.zeilen.length === 0) && <p className="text-xs text-gray-500">Nichts im Zeitraum.</p>}
    </div>
  );
}

function fmt(n: number): string {
  return new Intl.NumberFormat('de-DE').format(n);
}

export default function ChannelBreakdown({ channels, aiSources = [], from, to, projectId }: Props) {
  const [offen, setOffen] = useState<string | null>(null);
  const sorted = [...channels].sort((a, b) => b.visitors - a.visitors);
  const aiTotal = aiSources.reduce((s, a) => s + a.visitors, 0);
  const pieData = sorted
    .filter((c) => c.visitors > 0)
    .map((c) => ({ name: channelLabels[c.channel], value: c.visitors, channel: c.channel }));

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h2 className="text-sm font-semibold text-gray-900 mb-1">Traffic-Kanäle</h2>
      <p className="text-xs text-gray-500 mb-4">Woher die Besucher kommen – inkl. KI-Suche (ChatGPT, Perplexity &amp; Co.)</p>

      {sorted.length === 0 ? (
        <div className="h-40 flex items-center justify-center text-sm text-gray-400">Keine Daten im Zeitraum.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 items-center">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2} isAnimationActive={false}>
                {pieData.map((entry) => (
                  <Cell key={entry.channel} fill={channelColors[entry.channel]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: any) => [fmt(Number(value)), 'Besucher']}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 uppercase tracking-wider border-b border-gray-100">
                  <th className="py-2 pr-2 font-medium">Kanal</th>
                  <th className="py-2 px-2 font-medium text-right">Besucher</th>
                  {/* Bewusst NUR Formulare: Telefon-/Mail-Klicks werden ohne
                      Kanal gespeichert (t.js setzt bei Nicht-Seitenaufrufen
                      'direct'). Sie hier mitzuzählen hieße, sie alle dem Kanal
                      „Direkt" zuzuschlagen — eine erfundene Zuordnung. */}
                  <th
                    className="py-2 px-2 font-medium text-right"
                    title={'Nur Formular-Anfragen. Telefon- und Mail-Klicks lassen sich keinem Kanal zuordnen und stehen deshalb nur in der Gesamtzahl oben.'}
                  >
                    Formulare
                  </th>
                  <th className="py-2 pl-2 font-medium text-right">Conv.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {sorted.map((c) => (
                  <Fragment key={c.channel}>
                    <tr
                      onClick={() => setOffen(offen === c.channel ? null : c.channel)}
                      className="cursor-pointer hover:bg-gray-50"
                      title={`Zeigt, was hinter „${channelLabels[c.channel]}“ steckt`}
                    >
                      <td className="py-2 pr-2">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: channelColors[c.channel] }} />
                          {channelLabels[c.channel]}
                          <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${offen === c.channel ? 'rotate-180' : ''}`} />
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmt(c.visitors)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmt(c.leads)}</td>
                      <td className="py-2 pl-2 text-right tabular-nums text-gray-500">
                        {(c.conversion_rate * 100).toFixed(1)} %
                      </td>
                    </tr>
                    {offen === c.channel && (
                      <tr>
                        <td colSpan={4} className="p-0">
                          <KanalDetail channel={c.channel} from={from} to={to} projectId={projectId} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* KI-Suche aufgeschlüsselt: welche KI-Suchmaschine bringt die Besucher */}
      {aiTotal > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <h3 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: channelColors.ai }} />
            KI-Suche im Detail
          </h3>
          <div className="space-y-1.5">
            {aiSources.map((s) => (
              <div key={s.referrer_domain} className="flex items-center gap-2">
                <span className="text-xs text-gray-600 w-36 shrink-0 truncate" title={s.referrer_domain}>
                  {aiSourceLabels[s.referrer_domain] || s.referrer_domain}
                </span>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.max(4, (s.visitors / aiTotal) * 100)}%`, backgroundColor: channelColors.ai }} />
                </div>
                <span className="text-xs tabular-nums text-gray-500 w-14 text-right">{fmt(s.visitors)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
